import { getAmountAndTermCalculator } from '../../../hooks/useApplicationSummary';
import { useAnalytics } from 'use-analytics';
import { useCalculatorContext } from './CalculatorContext';
import { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import Slider from '../Slider/Slider';
import styles from './Calculator.module.scss';
import useApplication from '../../../hooks/useApplication';
import useClient from '../../../hooks/useClient';
import useClientConstraints from '../../../hooks/useClientConstraints';

export const Form = ({ className }: { className?: string }) => {
  const {
    isLoading,
    amount,
    setAmount,
    days,
    setDays,
    analytics,
    onChange,
    hideDays,
    content,
  } = useCalculatorContext();
  const { client } = useClient();
  const { data: constraints } = useClientConstraints();
  const { data: application } = useApplication();
  const { track } = useAnalytics();

  const defaultFormValues = getAmountAndTermCalculator({
    constraints,
    application,
  });

  const handleAnalytics = useCallback(
    ({ slider, value }: { slider: 'amount' | 'days'; value: number }) => {
      if (analytics?.dontTrack) return;

      track('calculator', {
        description: slider,
        value: analytics?.dontTrackContent ? '<not_tracked>' : value,
      });
    },
    [analytics?.dontTrack, analytics?.dontTrackContent, track],
  );

  const handleChangeAmount = useCallback(
    (value: number) => {
      setAmount(value);
      onChange?.('amount', value);
      handleAnalytics({ slider: 'amount', value });
    },
    [handleAnalytics, onChange],
  );

  const handleChangeDays = useCallback(
    (value: number) => {
      setDays(value);
      onChange?.('days', value);
      handleAnalytics({ slider: 'days', value });
    },
    [handleAnalytics, onChange],
  );

  useEffect(() => {
    if (!constraints || !application) return;
    // give time to the calculator to replace the default value according to application and constraint
    setTimeout(() => {
      setAmount(defaultFormValues?.amount);
      setDays(defaultFormValues?.term);
    }, 100);
  }, [defaultFormValues?.amount, defaultFormValues?.term]);

  const clientLoanLimit = String((client?.loansCount || 0) + 1);

  return (
    <div
      className={classNames([
        styles.form,
        isLoading && styles.loading,
        className,
      ])}
      data-test="css-Edit-Request-Container"
    >
      {/* Amount */}
      <Slider
        className={styles.sliderElement}
        dataTest="amount"
        disabled={isLoading}
        loading={isLoading}
        max={
          Number(constraints?.loanLimits?.[clientLoanLimit]) ||
          constraints?.amountInterval?.max ||
          500
        }
        min={constraints?.amountInterval?.min || 100}
        step={constraints?.amountInterval?.step || 10}
        onChange={handleChangeAmount}
        units={content.amountUnit}
        value={amount}
      />
      {/* Days */}
      {!hideDays && (
        <>
          <Slider
            className={styles.sliderElement}
            dataTest="term"
            disabled={isLoading}
            loading={isLoading}
            max={constraints?.termInterval?.max || 30}
            min={constraints?.termInterval?.min || 7}
            step={constraints?.termInterval?.step || 1}
            onChange={handleChangeDays}
            units={content.daysUnit}
            value={days}
          />
        </>
      )}
    </div>
  );
};
